import { useEffect, useMemo, useState } from 'react';
import {
  challengeCopy,
  createBridgePrompt,
  nextChallengeCandidate,
  selectUtcChallenge,
  utcDayPeriod,
  type BridgeCandidate,
  type DailyDiscoveryPayload,
  type DailyDiscoveryTrace,
  type UtcDayPeriod,
} from '@entangled-vega/shared';

import {
  dailyDiscoveryDocumentMetadata,
  loadDailyDiscovery,
} from '../data/daily-discovery-loader.js';
import {
  createBrowserDeviceLibraryController,
  type StoredDailyChallengeResult,
} from '../lib/device-library.js';
import { guessMatchesStepCount, type StepGuessRange } from './FeaturedGuess.js';
import { SiteHeader } from './SiteHeader.js';

type ChallengeResource =
  | { status: 'loading'; snapshotDate: string | null }
  | { status: 'ready'; payload: DailyDiscoveryPayload }
  | { status: 'error'; snapshotDate: string | null };

function exactStoryUrl(trace: DailyDiscoveryTrace): string {
  const params = new URLSearchParams({
    from: trace.source,
    to: trace.target,
    story: trace.id,
  });
  return `/?${params.toString()}`;
}

function currentUtcDay(): UtcDayPeriod | null {
  try {
    return utcDayPeriod(Date.now());
  } catch {
    return null;
  }
}

function ChallengeFallback({
  snapshotDate,
  loading = false,
}: {
  snapshotDate: string | null;
  loading?: boolean;
}) {
  return (
    <div className="app-shell static-shell">
      <SiteHeader badge={loading ? 'Loading daily connection' : 'Snapshot fallback'} />
      <main id="main-content" className="static-page challenge-page">
        <a className="static-page__back" href="/">
          ← Return to the constellation
        </a>
        <header className="static-page__hero">
          <p className="eyebrow">Featured connection</p>
          <h1>
            {loading
              ? 'Loading the accepted daily portfolio…'
              : `Featured connection from data snapshot ${snapshotDate ?? 'unavailable'}`}
          </h1>
          <p className="prose__lead">
            {loading
              ? 'Current-day language appears only after the browser validates the UTC day and the exact-version portfolio.'
              : 'Daily rotation is unavailable, so this page is not claiming a current-day connection. The source-linked Explorer remains available.'}
          </p>
          {loading ? null : (
            <a className="button button--gold" href="/">
              Explore the constellation
            </a>
          )}
        </header>
      </main>
    </div>
  );
}

function hydratedPortfolio(payload: DailyDiscoveryPayload): BridgeCandidate[] {
  const peopleById = new Map(payload.people.map((person) => [person.id, person]));
  return payload.traces.map((trace) => ({
    ...trace,
    eligible: true,
    people: trace.people.map((personId) => {
      const person = peopleById.get(personId);
      if (person === undefined) {
        throw new Error(`Daily trace ${trace.id} references unavailable person ${personId}.`);
      }
      return {
        id: person.id,
        name: person.name,
        eligible: person.inclusionRole !== 'context',
      };
    }),
  }));
}

function DailyChallengeExperience({
  payload,
  period,
}: {
  payload: DailyDiscoveryPayload;
  period: UtcDayPeriod;
}) {
  const portfolio = useMemo(() => hydratedPortfolio(payload), [payload]);
  const selection = selectUtcChallenge(portfolio, `${period.date}T12:00:00Z`);
  if (selection.kind !== 'selected') {
    return <ChallengeFallback snapshotDate={payload.snapshotDate} />;
  }
  const candidate = selection.candidate;
  const trace = payload.traces.find(({ id }) => id === candidate.id);
  if (trace === undefined) {
    return <ChallengeFallback snapshotDate={payload.snapshotDate} />;
  }
  const peopleById = new Map(payload.people.map((person) => [person.id, person]));
  const source = peopleById.get(trace.source);
  const target = peopleById.get(trace.target);
  if (source === undefined || target === undefined) {
    return <ChallengeFallback snapshotDate={payload.snapshotDate} />;
  }

  return (
    <SelectedDailyChallengeExperience
      payload={payload}
      period={period}
      portfolio={portfolio}
      candidate={candidate}
      trace={trace}
      sourceName={source.name}
      targetName={target.name}
    />
  );
}

function SelectedDailyChallengeExperience({
  payload,
  period,
  portfolio,
  candidate,
  trace,
  sourceName,
  targetName,
}: {
  payload: DailyDiscoveryPayload;
  period: UtcDayPeriod;
  portfolio: readonly BridgeCandidate[];
  candidate: BridgeCandidate;
  trace: DailyDiscoveryTrace;
  sourceName: string;
  targetName: string;
}) {
  const peopleById = useMemo(
    () => new Map(payload.people.map((person) => [person.id, person])),
    [payload.people],
  );
  const bridge = createBridgePrompt({
    datasetVersion: payload.datasetVersion,
    candidate,
    portfolio,
    period,
  });
  const controller = useMemo(
    () =>
      createBrowserDeviceLibraryController({
        datasetVersion: payload.datasetVersion,
        validPersonIds: new Set(),
        validStories: new Map(),
        isValidTrace: () => false,
        preserveUnvalidatedReferences: true,
        currentDayKey: period.key,
        currentStoryId: trace.id,
      }),
    [payload.datasetVersion, period.key, trace.id],
  );
  const [result, setResult] = useState<StoredDailyChallengeResult>(() => {
    const stored = controller.snapshot().state.dailyChallenge;
    return (
      stored ?? {
        datasetVersion: payload.datasetVersion,
        dayKey: period.key,
        storyId: trace.id,
        revealed: false,
      }
    );
  });

  function updateResult(changes: Partial<StoredDailyChallengeResult>): void {
    const next = { ...result, ...changes };
    controller.setDailyChallenge(next);
    setResult(next);
  }

  function chooseStepGuess(stepGuess: StepGuessRange): void {
    updateResult({
      stepGuess,
      stepCorrect: guessMatchesStepCount(stepGuess, trace.steps),
    });
  }

  function chooseBridge(bridgeChoiceId: string): void {
    updateResult({
      bridgeChoiceId,
      ...(bridge === null ? {} : { bridgeCorrect: bridge.answerId === bridgeChoiceId }),
    });
  }

  function reset(): void {
    controller.setDailyChallenge(undefined);
    setResult({
      datasetVersion: payload.datasetVersion,
      dayKey: period.key,
      storyId: trace.id,
      revealed: false,
    });
  }

  const copy = challengeCopy({
    snapshotDate: payload.snapshotDate,
    selectedPeriod: period,
    currentPeriod: period,
  });
  const nextCandidate = nextChallengeCandidate(portfolio, candidate.id);
  const nextTrace =
    nextCandidate === null
      ? null
      : (payload.traces.find(({ id }) => id === nextCandidate.id) ?? null);
  const pathNames = trace.people.map((personId) => peopleById.get(personId)?.name ?? personId);
  const storageSnapshot = controller.snapshot();

  return (
    <div className="app-shell static-shell">
      <SiteHeader badge="Daily discovery" />
      <main id="main-content" className="static-page challenge-page">
        <a className="static-page__back" href="/">
          ← Return to the constellation
        </a>
        <header className="static-page__hero challenge-page__hero">
          <p className="eyebrow">{copy.kicker}</p>
          <h1>
            {sourceName} <span aria-hidden="true">→</span> {targetName}
          </h1>
          <p className="prose__lead">
            Guess the accepted path, then reveal the exact source-linked Explorer story. The daily
            choice rotates presentation over a dated graph; it does not claim live facts.
          </p>
          <dl className="snapshot-facts">
            <div>
              <dt>UTC day</dt>
              <dd>{period.date}</dd>
            </div>
            <div>
              <dt>Data snapshot</dt>
              <dd>{payload.snapshotDate}</dd>
            </div>
            <div>
              <dt>Saved result</dt>
              <dd>Current day only</dd>
            </div>
          </dl>
        </header>

        {storageSnapshot.notice === null ? null : (
          <p className="challenge-page__notice" role="status">
            {storageSnapshot.notice}
          </p>
        )}

        <section className="challenge-question" aria-labelledby="step-question">
          <p className="eyebrow">Guess the length</p>
          <h2 id="step-question">How many relationship steps connect them?</h2>
          <div className="challenge-options" role="group" aria-label="Choose a step range">
            {(
              [
                ['1-2', '1–2 steps'],
                ['3-4', '3–4 steps'],
                ['5-plus', '5 steps'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                className="button"
                type="button"
                aria-pressed={result.stepGuess === value}
                onClick={() => chooseStepGuess(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        {bridge === null ? null : (
          <section className="challenge-question" aria-labelledby="bridge-question">
            <p className="eyebrow">Guess the bridge</p>
            <h2 id="bridge-question">Which person appears inside this accepted path?</h2>
            <div className="challenge-options" role="group" aria-label="Choose a bridge person">
              {bridge.choices.map((choice) => (
                <button
                  key={choice.id}
                  className="button"
                  type="button"
                  aria-pressed={result.bridgeChoiceId === choice.id}
                  onClick={() => chooseBridge(choice.id)}
                >
                  {choice.name}
                </button>
              ))}
            </div>
            <p>
              Choices are public names from accepted featured paths. An unselected choice makes no
              claim about a real-world relationship.
            </p>
          </section>
        )}

        <div className="challenge-page__actions">
          <button
            className="button button--gold"
            type="button"
            onClick={() => updateResult({ revealed: true })}
          >
            Reveal the source-linked path
          </button>
          <button className="button" type="button" onClick={reset}>
            Reset today’s challenge
          </button>
        </div>

        {result.revealed ? (
          <>
            <section className="challenge-reveal" aria-labelledby="challenge-answer">
              <p className="eyebrow">Accepted path revealed</p>
              <h2 id="challenge-answer">{trace.steps} relationship steps</h2>
              {result.stepGuess === undefined ? null : (
                <p role="status">
                  {result.stepCorrect === true
                    ? 'Your step-range guess matches this snapshot.'
                    : 'This snapshot has a different step range.'}
                </p>
              )}
              {bridge === null || result.bridgeChoiceId === undefined ? null : (
                <p role="status">
                  {result.bridgeCorrect === true
                    ? 'Your bridge choice appears in this path.'
                    : `The accepted bridge answer is ${bridge.choices.find(({ id }) => id === bridge.answerId)?.name ?? 'shown in the path below'}.`}
                </p>
              )}
              <ol className="challenge-path" aria-label="People in the accepted path">
                {pathNames.map((name, index) => (
                  <li key={trace.people[index]}>
                    <span>Person {index + 1}</span>
                    <strong>{name}</strong>
                  </li>
                ))}
              </ol>
              <a className="button button--gold" href={exactStoryUrl(trace)}>
                Open relationship details and sources in the Explorer
              </a>
              {nextTrace === null ? null : (
                <a className="button" href={exactStoryUrl(nextTrace)}>
                  Try the next accepted connection
                </a>
              )}
            </section>

            <section className="challenge-collections" aria-labelledby="collections-heading">
              <p className="eyebrow">Unexpected connections</p>
              <h2 id="collections-heading">More from the accepted featured portfolio</h2>
              <p>
                These are deterministic views of the featured set, not factual filters over the full
                graph.
              </p>
              <div className="challenge-collections__grid">
                {payload.collections.map((collection) => (
                  <article key={collection.id}>
                    <h3>{collection.title}</h3>
                    <p>{collection.description}</p>
                    <ul>
                      {collection.traceIds.map((traceId) => {
                        const collectionTrace = payload.traces.find(({ id }) => id === traceId);
                        if (collectionTrace === undefined) return null;
                        const collectionSource = peopleById.get(collectionTrace.source);
                        const collectionTarget = peopleById.get(collectionTrace.target);
                        if (collectionSource === undefined || collectionTarget === undefined) {
                          return null;
                        }
                        return (
                          <li key={traceId}>
                            <a href={exactStoryUrl(collectionTrace)}>
                              {collectionSource.name} <span aria-hidden="true">→</span>{' '}
                              {collectionTarget.name}
                              <small>{collectionTrace.steps} steps</small>
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </article>
                ))}
              </div>
            </section>
          </>
        ) : null}

        <footer className="static-page__footer">
          <span>Daily results and Saved activity stay on this device and are never synced.</span>
          <a href="/privacy">Privacy &amp; Legal</a>
        </footer>
      </main>
    </div>
  );
}

export function ChallengePage() {
  const metadata = useMemo(() => dailyDiscoveryDocumentMetadata(), []);
  const [resource, setResource] = useState<ChallengeResource>({
    status: 'loading',
    snapshotDate: metadata.snapshotDate,
  });
  const [period, setPeriod] = useState<UtcDayPeriod | null>(currentUtcDay);

  useEffect(() => {
    if (metadata.assetPath === null) {
      setResource({ status: 'error', snapshotDate: metadata.snapshotDate });
      return;
    }
    const controller = new AbortController();
    void loadDailyDiscovery({
      assetPath: metadata.assetPath,
      signal: controller.signal,
    }).then(
      (payload) => {
        if (!controller.signal.aborted) {
          setResource(
            metadata.snapshotDate !== null && payload.snapshotDate === metadata.snapshotDate
              ? { status: 'ready', payload }
              : { status: 'error', snapshotDate: metadata.snapshotDate },
          );
        }
      },
      () => {
        if (!controller.signal.aborted) {
          setResource({ status: 'error', snapshotDate: metadata.snapshotDate });
        }
      },
    );
    return () => controller.abort();
  }, [metadata.assetPath, metadata.snapshotDate]);

  useEffect(() => {
    if (period === null) return;
    const now = Date.now();
    if (!Number.isFinite(now)) {
      setPeriod(null);
      return;
    }
    const milliseconds = Math.max(100, (period.index + 1) * 86_400_000 - now + 50);
    const timeout = window.setTimeout(() => setPeriod(currentUtcDay()), milliseconds);
    return () => window.clearTimeout(timeout);
  }, [period]);

  if (resource.status === 'loading') {
    return <ChallengeFallback snapshotDate={resource.snapshotDate} loading />;
  }
  if (resource.status === 'error' || period === null) {
    return (
      <ChallengeFallback
        snapshotDate={
          resource.status === 'ready' ? resource.payload.snapshotDate : resource.snapshotDate
        }
      />
    );
  }
  return (
    <DailyChallengeExperience
      key={`${resource.payload.datasetVersion}:${period.key}`}
      payload={resource.payload}
      period={period}
    />
  );
}
